/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/ShiftZoneTargetsPage.tsx
 * ----------------------------------------------------------------------------
 * ADR-0041 F5a+F5b — Editor de alvos de dotacao por zona (ShiftZoneTarget)
 * com painel de disponibilidade ao vivo (availability-summary, F4).
 *
 * - Esquerda: escolhe (shift_template, warehouse) + camada temporal via abas
 *   (Base | Por dia [weekday] | Por periodo [2 datas]). Grelha das 9 zonas
 *   com min/max editaveis; Guardar cria/atualiza por zona (validate F3a -> 400).
 * - Direita: painel de cobertura para uma DATA propria (independente da camada);
 *   consome availability-summary -> totais + planeado-vs-alvo por zona (sub/sobre/ok).
 * - fetch() + buildAuthHeaders (molde da ShiftTemplatesPage).
 * ========================================================================== */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const API_BASE =
  (import.meta as any).env.VITE_API_BASE_URL?.replace(/\/$/, '') || '/api'
const WF_BASE = `${API_BASE}/workforce`

function buildAuthHeaders(contentTypeJson = true): HeadersInit {
  const state: any = (useAuthStore as any)?.getState?.() ?? {}
  const token: string | undefined = state.accessToken
  const headers: HeadersInit = {}
  if (contentTypeJson) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

const PROCESS_AREAS: { code: string; label: string }[] = [
  { code: 'RCV', label: 'Recebimento' },
  { code: 'PTW', label: 'Arrumação' },
  { code: 'PCK', label: 'Picking' },
  { code: 'RPL', label: 'Reposição' },
  { code: 'PKG', label: 'Embalagem' },
  { code: 'SHP', label: 'Expedição' },
  { code: 'INV', label: 'Inventário' },
  { code: 'MFC', label: 'Automação (MFC)' },
  { code: 'OTH', label: 'Outro' },
]

const WEEKDAYS = [
  { v: 0, label: 'Segunda' },
  { v: 1, label: 'Terça' },
  { v: 2, label: 'Quarta' },
  { v: 3, label: 'Quinta' },
  { v: 4, label: 'Sexta' },
  { v: 5, label: 'Sábado' },
  { v: 6, label: 'Domingo' },
]

type Layer = 'base' | 'weekday' | 'period'

type ShiftTemplate = { id: number; code: string; name: string }
type Warehouse = { id: number; code: string; name: string }

type Row = {
  id: number | null
  min: string
  max: string
}

type ZoneSummary = {
  process_area: string
  planeado: number
  min: number | null
  max: number | null
  estado: 'sub' | 'sobre' | 'ok'
}

type Summary = {
  date: string
  totais: { operadores: number; ausentes: number; disponiveis: number }
  por_zona: ZoneSummary[]
}

function emptyRows(): Record<string, Row> {
  const r: Record<string, Row> = {}
  for (const z of PROCESS_AREAS) r[z.code] = { id: null, min: '', max: '' }
  return r
}

function hojeISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function labelZona(code: string): string {
  const z = PROCESS_AREAS.find((x) => x.code === code)
  return z ? z.label : code
}

export default function ShiftZoneTargetsPage() {
  const [searchParams] = useSearchParams()
  const [templates, setTemplates] = useState<ShiftTemplate[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [templateId, setTemplateId] = useState<string>('')
  const [warehouseId, setWarehouseId] = useState<string>('')

  const [layer, setLayer] = useState<Layer>('base')
  const [weekday, setWeekday] = useState<number>(0)
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const [rows, setRows] = useState<Record<string, Row>>(emptyRows())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  // F5b: painel de disponibilidade (data propria, independente da camada).
  const [panelDate, setPanelDate] = useState<string>(hojeISO())
  const [summary, setSummary] = useState<Summary | null>(null)
  const [panelLoading, setPanelLoading] = useState(false)

  // F5c: dialogo de replicacao (fonte = a pagina atual).
  const [showReplicate, setShowReplicate] = useState(false)
  const [repMode, setRepMode] = useState<'base' | 'weekday' | 'period'>('base')
  const [repTargets, setRepTargets] = useState<string[]>([]) // ids de templates destino
  const [repWeekdays, setRepWeekdays] = useState<number[]>([])
  const [repFrom, setRepFrom] = useState<string>('')
  const [repTo, setRepTo] = useState<string>('')
  const [repBusy, setRepBusy] = useState(false)
  const [repMsg, setRepMsg] = useState<string | null>(null)
  const [repError, setRepError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const [rt, rw] = await Promise.all([
          fetch(`${WF_BASE}/shift-templates/?ordering=code`, {
            headers: buildAuthHeaders(false),
          }),
          fetch(`${API_BASE}/core/warehouses/?ordering=code`, {
            headers: buildAuthHeaders(false),
          }),
        ])
        const dt = await rt.json()
        const dw = await rw.json()
        const tpls: ShiftTemplate[] = dt.results ?? dt
        const whs: Warehouse[] = dw.results ?? dw
        setTemplates(tpls)
        setWarehouses(whs)
        const preTpl = searchParams.get('shift_template')
        if (preTpl && tpls.some((t) => String(t.id) === preTpl)) {
          setTemplateId(preTpl)
        } else if (tpls.length) {
          setTemplateId(String(tpls[0].id))
        }
        if (whs.length) setWarehouseId(String(whs[0].id))
      } catch {
        setError('Não foi possível carregar modelos/armazéns.')
      }
    })()
  }, [searchParams])

  useEffect(() => {
    if (!templateId || !warehouseId) return
    if (layer === 'period' && (!dateFrom || !dateTo)) {
      setRows(emptyRows())
      return
    }
    loadTargets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, warehouseId, layer, weekday, dateFrom, dateTo])

  // F5b: recarrega o painel quando muda template/warehouse/data-do-painel.
  useEffect(() => {
    if (!templateId || !warehouseId || !panelDate) {
      setSummary(null)
      return
    }
    loadSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, warehouseId, panelDate])

  function layerQuery(): string {
    if (layer === 'weekday') return `&weekday=${weekday}`
    if (layer === 'period') return `&date_from=${dateFrom}&date_to=${dateTo}`
    return ''
  }

  async function loadTargets() {
    setLoading(true)
    setError(null)
    setOkMsg(null)
    try {
      const url = `${WF_BASE}/shift-zone-targets/?shift_template=${templateId}&warehouse=${warehouseId}${layerQuery()}`
      const res = await fetch(url, { headers: buildAuthHeaders(false) })
      if (!res.ok) throw new Error(String(res.status))
      const data = await res.json()
      const list: any[] = data.results ?? data
      const next = emptyRows()
      for (const t of list) {
        const area = t.process_area
        if (next[area]) {
          next[area] = {
            id: t.id,
            min: t.min_operators == null ? '' : String(t.min_operators),
            max: t.max_operators == null ? '' : String(t.max_operators),
          }
        }
      }
      setRows(next)
    } catch {
      setError('Não foi possível carregar os alvos desta camada.')
    } finally {
      setLoading(false)
    }
  }

  async function loadSummary() {
    setPanelLoading(true)
    try {
      const url = `${WF_BASE}/operators/availability-summary/?shift_template=${templateId}&warehouse=${warehouseId}&date=${panelDate}`
      const res = await fetch(url, { headers: buildAuthHeaders(false) })
      if (!res.ok) throw new Error(String(res.status))
      const data: Summary = await res.json()
      setSummary(data)
    } catch {
      setSummary(null)
    } finally {
      setPanelLoading(false)
    }
  }

  function setCell(area: string, field: 'min' | 'max', value: string) {
    setRows((prev) => ({ ...prev, [area]: { ...prev[area], [field]: value } }))
  }

  function temporalBody(): Record<string, any> {
    if (layer === 'weekday') return { weekday, date_from: null, date_to: null }
    if (layer === 'period')
      return { weekday: null, date_from: dateFrom, date_to: dateTo }
    return { weekday: null, date_from: null, date_to: null }
  }

  async function save() {
    if (!templateId || !warehouseId) return
    setSaving(true)
    setError(null)
    setOkMsg(null)
    try {
      const tasks: Promise<Response>[] = []
      for (const z of PROCESS_AREAS) {
        const row = rows[z.code]
        if (row.min === '') continue
        const payload: Record<string, any> = {
          shift_template: Number(templateId),
          warehouse: Number(warehouseId),
          process_area: z.code,
          min_operators: Number(row.min),
          max_operators: row.max === '' ? null : Number(row.max),
          ...temporalBody(),
        }
        if (row.id == null) {
          tasks.push(
            fetch(`${WF_BASE}/shift-zone-targets/`, {
              method: 'POST',
              headers: buildAuthHeaders(true),
              body: JSON.stringify(payload),
            }),
          )
        } else {
          tasks.push(
            fetch(`${WF_BASE}/shift-zone-targets/${row.id}/`, {
              method: 'PATCH',
              headers: buildAuthHeaders(true),
              body: JSON.stringify(payload),
            }),
          )
        }
      }
      const results = await Promise.all(tasks)
      const bad = results.find((r) => !r.ok)
      if (bad) {
        let detail = 'Erro ao guardar (verifica a combinação temporal).'
        try {
          const j = await bad.json()
          if (j?.detail) detail = j.detail
        } catch {
          /* sem corpo JSON */
        }
        setError(detail)
      } else {
        setOkMsg('Alvos guardados.')
        await loadTargets()
        await loadSummary()
      }
    } catch {
      setError('Falha de rede ao guardar.')
    } finally {
      setSaving(false)
    }
  }

  function toggleRepTarget(id: string) {
    setRepTargets((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  function toggleRepWeekday(d: number) {
    setRepWeekdays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    )
  }

  async function replicate() {
    if (!templateId || !warehouseId || repTargets.length === 0) return
    setRepBusy(true)
    setRepMsg(null)
    setRepError(null)
    try {
      const body: Record<string, any> = {
        source: {
          shift_template: Number(templateId),
          warehouse: Number(warehouseId),
        },
        targets: repTargets.map((tid) => ({
          shift_template: Number(tid),
          warehouse: Number(warehouseId),
        })),
      }
      if (repMode === 'weekday') {
        if (repWeekdays.length === 0) {
          setRepError('Escolhe pelo menos um dia da semana.')
          setRepBusy(false)
          return
        }
        body.temporal = { mode: 'weekday', weekdays: repWeekdays }
      } else if (repMode === 'period') {
        if (!repFrom || !repTo) {
          setRepError('Define o periodo (inicio e fim).')
          setRepBusy(false)
          return
        }
        body.temporal = { mode: 'period', date_from: repFrom, date_to: repTo }
      }
      const res = await fetch(`${WF_BASE}/shift-zone-targets/replicate/`, {
        method: 'POST',
        headers: buildAuthHeaders(true),
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        let detail = 'Nao foi possivel replicar (verifica os campos).'
        try {
          const j = await res.json()
          if (j?.detail) detail = j.detail
        } catch {
          /* sem corpo JSON */
        }
        setRepError(detail)
      } else {
        const j = await res.json()
        setRepMsg(
          `Replicado: ${j.created ?? 0} criados, ${j.updated ?? 0} atualizados.`,
        )
        await loadTargets()
        await loadSummary()
      }
    } catch {
      setRepError('Falha de rede ao replicar.')
    } finally {
      setRepBusy(false)
    }
  }

  function estadoCor(estado: string): {
    cor: string
    bg: string
    sinal: string
  } {
    if (estado === 'sub') return { cor: '#a32d2d', bg: '#fcebeb', sinal: '▼' }
    if (estado === 'sobre') return { cor: '#854f0b', bg: '#faeeda', sinal: '▲' }
    return { cor: '#0f6e56', bg: '#e1f5ee', sinal: '✓' }
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: '1rem' }}>
        Alvos de dotação por zona
      </h1>

      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '1rem',
        }}
      >
        <div>
          <Label>Modelo de turno</Label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            style={{ minWidth: 180 }}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Armazém</Label>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* Coluna esquerda: editor de alvos */}
        <div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              borderBottom: '0.5px solid #ccc',
              paddingBottom: 8,
              marginBottom: '0.75rem',
            }}
          >
            <Button
              variant={layer === 'base' ? 'default' : 'outline'}
              onClick={() => setLayer('base')}
            >
              Base
            </Button>
            <Button
              variant={layer === 'weekday' ? 'default' : 'outline'}
              onClick={() => setLayer('weekday')}
            >
              Por dia
            </Button>
            <Button
              variant={layer === 'period' ? 'default' : 'outline'}
              onClick={() => setLayer('period')}
            >
              Por período
            </Button>
          </div>

          {layer === 'weekday' && (
            <div style={{ marginBottom: '0.75rem' }}>
              <Label>Dia da semana</Label>
              <select
                value={weekday}
                onChange={(e) => setWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {layer === 'period' && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-end',
                marginBottom: '0.75rem',
              }}
            >
              <div>
                <Label>Início</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label>Fim</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && (
            <div style={{ color: '#a32d2d', marginBottom: '0.5rem' }}>
              {error}
            </div>
          )}
          {okMsg && (
            <div style={{ color: '#0f6e56', marginBottom: '0.5rem' }}>
              {okMsg}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zona</TableHead>
                <TableHead style={{ width: 100 }}>Mínimo</TableHead>
                <TableHead style={{ width: 100 }}>Máximo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PROCESS_AREAS.map((z) => (
                <TableRow key={z.code}>
                  <TableCell>
                    {z.label}{' '}
                    <span style={{ color: '#888', fontSize: 12 }}>
                      ({z.code})
                    </span>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      value={rows[z.code].min}
                      onChange={(e) => setCell(z.code, 'min', e.target.value)}
                      disabled={loading}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={rows[z.code].max}
                      onChange={(e) => setCell(z.code, 'max', e.target.value)}
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div style={{ marginTop: '1rem' }}>
            <Button
              onClick={save}
              disabled={saving || loading || !templateId || !warehouseId}
            >
              {saving ? 'A guardar…' : 'Guardar alvos'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowReplicate(true)}
              disabled={!templateId || !warehouseId}
              style={{ marginLeft: 8 }}
            >
              Replicar…
            </Button>
          </div>
        </div>

        {/* Coluna direita: painel de cobertura (F5b, availability-summary) */}
        <div
          style={{
            border: '0.5px solid #ddd',
            borderRadius: 8,
            padding: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginBottom: '0.75rem',
            }}
          >
            <h2 style={{ fontSize: 16, fontWeight: 500 }}>Cobertura</h2>
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={panelDate}
                onChange={(e) => setPanelDate(e.target.value)}
              />
            </div>
          </div>

          {panelLoading && <div style={{ color: '#888' }}>A carregar…</div>}
          {!panelLoading && !summary && (
            <div style={{ color: '#888' }}>Sem dados para esta data.</div>
          )}
          {!panelLoading && summary && (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginBottom: '1rem',
                  fontSize: 14,
                }}
              >
                <div>
                  Operadores: <b>{summary.totais.operadores}</b>
                </div>
                <div>
                  Ausentes: <b>{summary.totais.ausentes}</b>
                </div>
                <div>
                  Disponíveis: <b>{summary.totais.disponiveis}</b>
                </div>
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {summary.por_zona.map((z) => {
                  const c = estadoCor(z.estado)
                  return (
                    <div
                      key={z.process_area}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: c.bg,
                        color: c.cor,
                        padding: '4px 8px',
                        borderRadius: 6,
                        fontSize: 14,
                      }}
                    >
                      <span>{labelZona(z.process_area)}</span>
                      <span>
                        plan {z.planeado} / alvo {z.min ?? '—'}
                        {z.max != null ? `–${z.max}` : ''} {c.sinal}
                      </span>
                    </div>
                  )
                })}
                {summary.por_zona.length === 0 && (
                  <div style={{ color: '#888' }}>
                    Nenhuma zona com alvo ou planeamento.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <Dialog open={showReplicate} onOpenChange={setShowReplicate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Replicar alvos</DialogTitle>
            </DialogHeader>

            <div
              style={{
                background: '#f3f3f1',
                borderRadius: 8,
                padding: '8px 12px',
                marginBottom: 14,
                fontSize: 13,
              }}
            >
              A copiar de{' '}
              <b>
                {templates.find((t) => String(t.id) === templateId)?.code ??
                  '—'}
              </b>{' '}
              no armazém atual.
            </div>

            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
              Para que modelos
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginBottom: 14,
              }}
            >
              {templates
                .filter((t) => String(t.id) !== templateId)
                .map((t) => (
                  <label
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={repTargets.includes(String(t.id))}
                      onChange={() => toggleRepTarget(String(t.id))}
                    />
                    {t.code} — {t.name}
                  </label>
                ))}
              {templates.filter((t) => String(t.id) !== templateId).length ===
                0 && (
                <div style={{ color: '#888', fontSize: 13 }}>
                  Não há outros modelos para onde replicar.
                </div>
              )}
            </div>

            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
              Quando se aplica
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <Button
                variant={repMode === 'base' ? 'default' : 'outline'}
                onClick={() => setRepMode('base')}
              >
                Base
              </Button>
              <Button
                variant={repMode === 'weekday' ? 'default' : 'outline'}
                onClick={() => setRepMode('weekday')}
              >
                Por dias
              </Button>
              <Button
                variant={repMode === 'period' ? 'default' : 'outline'}
                onClick={() => setRepMode('period')}
              >
                Por período
              </Button>
            </div>

            {repMode === 'base' && (
              <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
                Os alvos copiados valem sempre (camada base).
              </div>
            )}

            {repMode === 'weekday' && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginBottom: 14,
                }}
              >
                {WEEKDAYS.map((d) => (
                  <label
                    key={d.v}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 13,
                      border: '0.5px solid #ccc',
                      borderRadius: 6,
                      padding: '4px 8px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={repWeekdays.includes(d.v)}
                      onChange={() => toggleRepWeekday(d.v)}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
            )}

            {repMode === 'period' && (
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-end',
                  marginBottom: 14,
                }}
              >
                <div>
                  <Label>Início</Label>
                  <Input
                    type="date"
                    value={repFrom}
                    onChange={(e) => setRepFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Fim</Label>
                  <Input
                    type="date"
                    value={repTo}
                    onChange={(e) => setRepTo(e.target.value)}
                  />
                </div>
              </div>
            )}

            {repError && (
              <div style={{ color: '#a32d2d', fontSize: 13, marginBottom: 8 }}>
                {repError}
              </div>
            )}
            {repMsg && (
              <div style={{ color: '#0f6e56', fontSize: 13, marginBottom: 8 }}>
                {repMsg}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReplicate(false)}>
                Fechar
              </Button>
              <Button
                onClick={replicate}
                disabled={repBusy || repTargets.length === 0}
              >
                {repBusy ? 'A replicar…' : 'Replicar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
